/**
 * Test ID utilities for the IntensityScaleV2 component.
 *
 * Pattern: all test IDs are generated via named setter functions so component
 * and tests can never drift apart.
 */

import { INTENSITY_POSITIONS, type IntensityPosition } from "@shared/types/intensity";
import { generateTestId } from "@client-shared";

import { INTENSITY_BLOCK_COLORS } from "./intensity-scale.utils";

export const setIntensityScaleV2TestId = (identifier: string): string => generateTestId("IntensityScaleV2", identifier);

export const setIntensityMarkerV2TestId = (index: number): string => generateTestId("IntensityScaleV2", "marker", index);

export const setIntensityLabelV2TestId = (position: IntensityPosition): string => generateTestId("IntensityScaleV2", "label", position);

/**
 * Hard-edged CSS gradient for the slider track: each of the nine positions
 * gets a solid band of its own colour, centred on that position's slider
 * stop (rather than nine equal-width bands), so the thumb always rests
 * inside its own colour instead of straddling a boundary.
 */
export const intensitySegmentedTrackGradient = (): string => {
  const lastIndex = INTENSITY_POSITIONS.length - 1;
  // Stops are evenly spaced, so the boundary between stop N and N+1 (the
  // midpoint) is derivable directly — no need to index into a stops array.
  const boundaryAfter = (index: number): number => ((index + 0.5) / lastIndex) * 100;

  const bands = INTENSITY_POSITIONS.flatMap((position, index) => {
    const start = index === 0 ? 0 : boundaryAfter(index - 1);
    const end = index === lastIndex ? 100 : boundaryAfter(index);
    const color = INTENSITY_BLOCK_COLORS[position];
    return [`${color} ${start}%`, `${color} ${end}%`];
  });

  return `linear-gradient(to right, ${bands.join(", ")})`;
};
